import { Card } from 'components/Card';
import { CardRow } from 'components/CardRow';
import { ModalScreenProps, ModalScreens } from 'components/Navigation/types';
import { Page } from 'components/Page';
import { useEsc } from 'hooks/useEsc';
import { theme } from 'lib/theme';
import React from 'react';
import { useDispatch } from 'react-redux';
import {
  ALERT,
  doEngageContactEvacuationInfoSave,
  NAVIGATE_MODAL_BACK,
} from 'store/actions';
import { View } from 'react-native';
import { format, startOfDay } from 'date-fns';
import { Txt } from 'components/Txt';
import { DateField } from 'components/Field/DateField';
import { API_DATE_FORMAT } from 'lib/constants';
import { TextAreaFieldWithCharacterCount } from 'components/Field/TextAreaFieldWithCharacterCount';
import {
  EmergencyEvacuationSharingStatus,
  EngageContactEvacuationInfo,
} from 'types/DTOs/BlockManagerDTOs';
import { useForceUpdate } from 'hooks/useForceUpdate';
import { dateToIsoString, isoDateStringToDate, arrayJoin } from 'lib/riskbase';
import { ISODateString } from 'types/types';
import { useConfirmUnsaved } from 'hooks/useConfirm';
import { isEqual } from 'lodash';
import { PageHeader } from 'components/PageHeader';
import { GapSmall } from 'components/GapSmall';
import { GapMedium } from 'components/GapMedium';
import { TextAreaField } from 'components/Field/TextAreaField/TextAreaField';

const EmergencyEvacuationSharingStatusLabels: Record<
  EmergencyEvacuationSharingStatus,
  string
> = {
  [EmergencyEvacuationSharingStatus.NOT_ASKED]: 'Not asked',
  [EmergencyEvacuationSharingStatus.ASKED_WAITING]: 'Asked, awaiting response',
  [EmergencyEvacuationSharingStatus.AGREED]: 'Agreed',
  [EmergencyEvacuationSharingStatus.DISAGREED]: 'Disagreed',
};

const validateData = (data: ModalEngageContactEvacuationInfo) => {
  const missingValues: string[] = [];
  const messages: string[] = [];

  if (data.hasAgreedEmergencyEvacuationStatement) {
    if (
      !data.emergencyEvacuationStatement ||
      data.emergencyEvacuationStatement.trim() === ''
    ) {
      missingValues.push('Emergency Evacuation Statement (EES)');
      messages.push('provide the Emergency Evacuation Statement (EES)');
    }
  }

  if (
    data.emergencyEvacuationSharingStatus !==
    EmergencyEvacuationSharingStatus.NOT_ASKED
  ) {
    if (
      !data.emergencyEvacuationAssistanceSummary ||
      data.emergencyEvacuationAssistanceSummary.trim() === ''
    ) {
      missingValues.push('Emergency Evacuation Assistance Summary (EEAS)');
      messages.push(
        'provide the Emergency Evacuation Assistance Summary (EEAS)',
      );
    }
    // Defense for bad data/ state changes -> technically not possible to reach from normal UI flow
    if (
      data.emergencyEvacuationSharingAgreedDate &&
      startOfDay(
        isoDateStringToDate(data.emergencyEvacuationSharingAgreedDate),
      ) > startOfDay(new Date())
    ) {
      missingValues.push('Date Recorded');
      messages.push('record a Date Recorded that is today or in the past');
    }
  }

  return {
    hasErrors: missingValues.length > 0,
    title: `Missing ${arrayJoin(missingValues, ', ', ' and ')}`,
    text: `Please ${arrayJoin(messages, ', ', ' and ')}.`,
  };
};

type ModalEngageContactEvacuationInfo = EngageContactEvacuationInfo & {
  hasAgreedEmergencyEvacuationStatement: boolean;
};

function getSubmitData(
  data: ModalEngageContactEvacuationInfo,
): EngageContactEvacuationInfo {
  return !data.hasSupportNeeds
    ? {
        hasSupportNeeds: false,
        emergencyEvacuationStatementAgreedDate: null,
        emergencyEvacuationSharingStatus:
          EmergencyEvacuationSharingStatus.NOT_ASKED,
        emergencyEvacuationSharingAgreedDate: null,
        emergencyEvacuationAssistanceSummary: '',
        emergencyEvacuationStatement: '',
        hasCognitiveImpairment: false,
        hasHearingImpairment: false,
        hasMobilityLimitation: false,
        hasSocialAnxiety: false,
        hasVisualImpairment: false,
        hasOtherSupportNeed: false,
        otherSupportNeed: null,
      }
    : {
        hasSupportNeeds: data.hasSupportNeeds,
        emergencyEvacuationStatementAgreedDate:
          data.hasAgreedEmergencyEvacuationStatement
            ? data.emergencyEvacuationStatementAgreedDate
            : null,
        emergencyEvacuationSharingStatus: data.emergencyEvacuationSharingStatus,
        emergencyEvacuationSharingAgreedDate:
          data.emergencyEvacuationSharingStatus !==
          EmergencyEvacuationSharingStatus.NOT_ASKED
            ? data.emergencyEvacuationSharingAgreedDate
            : null,
        emergencyEvacuationAssistanceSummary:
          data.emergencyEvacuationAssistanceSummary,
        emergencyEvacuationStatement: data.emergencyEvacuationStatement,
        hasCognitiveImpairment: data.hasCognitiveImpairment,
        hasHearingImpairment: data.hasHearingImpairment,
        hasMobilityLimitation: data.hasMobilityLimitation,
        hasSocialAnxiety: data.hasSocialAnxiety,
        hasVisualImpairment: data.hasVisualImpairment,
        hasOtherSupportNeed: data.hasOtherSupportNeed,
        otherSupportNeed: data.hasOtherSupportNeed
          ? data.otherSupportNeed
          : null,
      };
}

export const EngageContactEmergencyEvacuationModal: React.FC<
  ModalScreenProps<ModalScreens.EngageContactEmergencyEvacuation>
> = ({ route }) => {
  const dispatch = useDispatch();
  const forceUpdate = useForceUpdate();
  const {
    contactName,
    contactId,
    flatId,
    propertyId,
    data: initialData,
    key,
  } = route.params;
  const confirmUnsaved = useConfirmUnsaved();

  const data = React.useRef<ModalEngageContactEvacuationInfo>({
    ...initialData,
    hasAgreedEmergencyEvacuationStatement:
      !!initialData.emergencyEvacuationStatementAgreedDate,
    emergencyEvacuationStatementAgreedDate:
      !!initialData.emergencyEvacuationStatementAgreedDate
        ? initialData.emergencyEvacuationStatementAgreedDate
        : dateToIsoString(new Date()),
    emergencyEvacuationSharingStatus:
      initialData.emergencyEvacuationSharingStatus,
    emergencyEvacuationSharingAgreedDate:
      !!initialData.emergencyEvacuationSharingAgreedDate
        ? initialData.emergencyEvacuationSharingAgreedDate
        : dateToIsoString(new Date()),
  });

  const handleSave = React.useCallback(() => {
    const { hasErrors, title, text } = validateData(data.current);

    if (hasErrors) {
      dispatch(
        ALERT({
          title,
          text,
        }),
      );
      return;
    }

    const newData = getSubmitData(data.current);

    if (isEqual(newData, initialData)) {
      dispatch(NAVIGATE_MODAL_BACK());
      return;
    }

    dispatch(
      doEngageContactEvacuationInfoSave({
        key,
        propertyId,
        flatId,
        contactId,
        info: newData,
      }),
    );
  }, [dispatch, key, propertyId, flatId, contactId, initialData, data]);

  const handleClose = React.useCallback(() => {
    if (!isEqual(getSubmitData(data.current), initialData)) {
      confirmUnsaved({
        onSave: () => handleSave(),
        onDiscard: () => dispatch(NAVIGATE_MODAL_BACK()),
      });
    } else {
      dispatch(NAVIGATE_MODAL_BACK());
    }
  }, [dispatch, confirmUnsaved, handleSave, data, initialData]);

  const updateCheckValue = React.useCallback(
    (
      field: keyof Pick<
        ModalEngageContactEvacuationInfo,
        | 'hasSupportNeeds'
        | 'hasSocialAnxiety'
        | 'hasMobilityLimitation'
        | 'hasHearingImpairment'
        | 'hasCognitiveImpairment'
        | 'hasVisualImpairment'
        | 'hasOtherSupportNeed'
        | 'hasAgreedEmergencyEvacuationStatement'
      >,
      value: boolean,
    ) => {
      data.current[field] = value;
      forceUpdate();
    },
    [data, forceUpdate],
  );

  const updateSharingStatus = React.useCallback(
    (value: EmergencyEvacuationSharingStatus) => {
      data.current.emergencyEvacuationSharingStatus = value;
      forceUpdate();
    },
    [data, forceUpdate],
  );

  const updateTextValue = React.useCallback(
    (
      field: keyof Pick<
        typeof data.current,
        | 'emergencyEvacuationAssistanceSummary'
        | 'emergencyEvacuationStatement'
        | 'otherSupportNeed'
      >,
      value: string,
    ) => {
      data.current[field] = value;
      forceUpdate();
    },
    [data, forceUpdate],
  );

  const updateDateValue = React.useCallback(
    (
      field: keyof Pick<
        ModalEngageContactEvacuationInfo,
        | 'emergencyEvacuationStatementAgreedDate'
        | 'emergencyEvacuationSharingAgreedDate'
      >,
      value: Date | null,
    ) => {
      (data.current[field] as ISODateString | null) = value
        ? dateToIsoString(new Date(value))
        : null;
      forceUpdate();
    },
    [data, forceUpdate],
  );

  useEsc(handleClose);

  const shouldShowSharingDate =
    data.current.emergencyEvacuationSharingStatus !==
    EmergencyEvacuationSharingStatus.NOT_ASKED;
  const today = new Date();

  return (
    <Page
      scrollView
      header={
        <PageHeader
          title="Vulnerability, PEEP and PCFRA Management"
          subtitle={contactName}
          onPressSaveButton={handleSave}
          onPressCancelButton={handleClose}
        />
      }
    >
      <GapSmall>
        <Card
          label="Help Required When Evacuating"
          labelSize="large"
          explanation="This section describes any help this resident requires when evacuating their flat in an emergency."
        ></Card>
        <Card>
          <CardRow
            label="Resident requires help evacuating in an emergency"
            checkedValue={data.current.hasSupportNeeds}
            onPress={() =>
              updateCheckValue(
                'hasSupportNeeds',
                data.current.hasSupportNeeds ? false : true,
              )
            }
          />
        </Card>
        {data.current.hasSupportNeeds && (
          <>
            <Card>
              <CardRow
                label="Mobility Limitation"
                label2="e.g. needs help with stairs"
                checkedValue={data.current.hasMobilityLimitation}
                onPress={() =>
                  updateCheckValue(
                    'hasMobilityLimitation',
                    !data.current.hasMobilityLimitation,
                  )
                }
              />
              <CardRow
                label="Cognitive/Comprehension Difficulty"
                label2="e.g. struggles to follow instructions"
                checkedValue={data.current.hasCognitiveImpairment}
                onPress={() =>
                  updateCheckValue(
                    'hasCognitiveImpairment',
                    !data.current.hasCognitiveImpairment,
                  )
                }
              />
              <CardRow
                label="Social/Environmental Anxiety"
                label2="e.g. stress in crowds or panic attacks"
                checkedValue={data.current.hasSocialAnxiety}
                onPress={() =>
                  updateCheckValue(
                    'hasSocialAnxiety',
                    !data.current.hasSocialAnxiety,
                  )
                }
              />
              <CardRow
                label="Hearing Impairment"
                label2="e.g. may not hear the alarm"
                checkedValue={data.current.hasHearingImpairment}
                onPress={() =>
                  updateCheckValue(
                    'hasHearingImpairment',
                    !data.current.hasHearingImpairment,
                  )
                }
              />
              <CardRow
                label="Visual Impairment"
                label2="e.g. may not be able to navigate out unaided"
                checkedValue={data.current.hasVisualImpairment}
                onPress={() =>
                  updateCheckValue(
                    'hasVisualImpairment',
                    !data.current.hasVisualImpairment,
                  )
                }
              />
              <CardRow
                label="Other Support Needs"
                label2="e.g. anything else this person would need assistance with in the event of a fire"
                checkedValue={data.current.hasOtherSupportNeed}
                onPress={() =>
                  updateCheckValue(
                    'hasOtherSupportNeed',
                    !data.current.hasOtherSupportNeed,
                  )
                }
              />
            </Card>
            {data.current.hasOtherSupportNeed && (
              <Card>
                <TextAreaField
                  placeholder="Describe other support needs required..."
                  onChange={(value) => {
                    updateTextValue('otherSupportNeed', value);
                  }}
                  maxLength={1000}
                  value={data.current.otherSupportNeed || ''}
                />
              </Card>
            )}
          </>
        )}
      </GapSmall>

      {data.current.hasSupportNeeds && (
        <GapMedium>
          <Card
            label="Emergency Evacuation Statement"
            labelSize="large"
            required={data.current.hasAgreedEmergencyEvacuationStatement}
            explanation={
              'An EES sets out what the resident should do in the event of a fire. ' +
              'Describe how the resident would evacuate in an emergency, including any assistance, equipment, or agreed actions needed.'
            }
            style={{ marginBottom: -30 }}
            explanationStyle={{ paddingRight: 100 }}
          ></Card>
          <GapSmall>
            <TextAreaFieldWithCharacterCount
              placeholder="No Emergency Evacuation Statement"
              required={data.current.hasAgreedEmergencyEvacuationStatement}
              onChange={(value) => {
                updateTextValue('emergencyEvacuationStatement', value);
              }}
              maxLength={1000}
              value={data.current.emergencyEvacuationStatement || ''}
            />
            <Card>
              <CardRow
                label="Resident has agreed to the Emergency Evacuation Statement"
                checkedValue={
                  data.current.hasAgreedEmergencyEvacuationStatement
                }
                onPress={() => {
                  updateCheckValue(
                    'hasAgreedEmergencyEvacuationStatement',
                    !data.current.hasAgreedEmergencyEvacuationStatement,
                  );
                }}
              />
              {data.current.hasAgreedEmergencyEvacuationStatement && (
                <CardRow>
                  <View
                    style={{
                      flexDirection: 'row',
                      alignItems: 'center',
                      marginVertical: -theme.padding,
                    }}
                  >
                    <Txt>Date Agreed</Txt>
                    <DateField
                      value={
                        data.current.emergencyEvacuationStatementAgreedDate
                          ? format(
                              isoDateStringToDate(
                                data.current
                                  .emergencyEvacuationStatementAgreedDate,
                              ),
                              API_DATE_FORMAT,
                            )
                          : ''
                      }
                      onChange={(value) => {
                        updateDateValue(
                          'emergencyEvacuationStatementAgreedDate',
                          new Date(value),
                        );
                      }}
                      style={{
                        backgroundColor: 'transparent',
                      }}
                      clearable={false}
                      showFieldTypeIcon={false}
                    />
                  </View>
                </CardRow>
              )}
            </Card>
          </GapSmall>
        </GapMedium>
      )}

      {data.current.hasSupportNeeds && (
        <GapMedium>
          <Card
            label="Evacuation Assistance Summary"
            labelSize="large"
            required={
              data.current.emergencyEvacuationSharingStatus !==
              EmergencyEvacuationSharingStatus.NOT_ASKED
            }
            explanation="Provide a brief summary of the level of assistance required for evacuation. This may be shared with the Fire and Rescue Service and must be short and clear."
            style={{ marginBottom: -30 }}
            explanationStyle={{ paddingRight: 100 }}
          ></Card>
          <TextAreaFieldWithCharacterCount
            placeholder="No Evacuation Assistance Summary"
            required={
              data.current.emergencyEvacuationSharingStatus !==
              EmergencyEvacuationSharingStatus.NOT_ASKED
            }
            onChange={(value) => {
              updateTextValue('emergencyEvacuationAssistanceSummary', value);
            }}
            maxLength={300}
            value={data.current.emergencyEvacuationAssistanceSummary}
          />

          <Card
            label="Sharing with FRS"
            explanation="Has the resident agreed to share relevant information with the Fire Rescue Service"
          >
            {Object.values(EmergencyEvacuationSharingStatus).map((value) => (
              <CardRow
                key={value}
                label={EmergencyEvacuationSharingStatusLabels[value]}
                radioValue={
                  data.current.emergencyEvacuationSharingStatus === value
                }
                onPress={() => updateSharingStatus(value)}
              />
            ))}
            {shouldShowSharingDate && (
              <CardRow>
                <View
                  style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    marginVertical: -theme.padding,
                  }}
                >
                  <Txt>Date Recorded</Txt>
                  <DateField
                    value={
                      data.current.emergencyEvacuationSharingAgreedDate
                        ? format(
                            isoDateStringToDate(
                              data.current.emergencyEvacuationSharingAgreedDate,
                            ),
                            API_DATE_FORMAT,
                          )
                        : ''
                    }
                    onChange={(value) => {
                      updateDateValue(
                        'emergencyEvacuationSharingAgreedDate',
                        new Date(value),
                      );
                    }}
                    style={{
                      backgroundColor: 'transparent',
                    }}
                    max={today}
                    clearable={false}
                    showFieldTypeIcon={false}
                  />
                </View>
              </CardRow>
            )}
          </Card>
        </GapMedium>
      )}
    </Page>
  );
};
